import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
  getAdoptionRate,
  getBookContentAnalysis,
  getClassComparison,
  getDashboard,
  getReadingGrowthTrend,
  getReadingMix,
  getReaderClass,
  getTeacherAdoption,
  getTopStudents,
  getTopTeachers,
} from "shared/services/campusAnalyticsService";
import {
  CampusDatum,
  GrowthMetric,
  KpiDatum,
  MixDimension,
  RangeDatum,
  StudentDatum,
  TeacherDatum,
} from "../headOffice/data";
import { rangeToWindow } from "../headOffice/useHeadOfficeData";
import {
  AssignmentView,
  mapAssignmentPipeline,
} from "../headOffice/apiMappers";
import {
  AdoptionView,
  ContentView,
  mapAdoption,
  mapComparison,
  mapContent,
  mapGrowth,
  mapKpis,
  mapReaderClasses,
  mapReadingMix,
  mapTeacherAdoption,
  mapTopStudents,
  mapTopTeachers,
} from "./apiMappers";

export { rangeToWindow };

/** unwrap the `{ status, statusCode, message, data }` envelope */
/**
 * Unwrap the `{ status, statusCode, message, data }` envelope.
 *
 * This API answers refusals with HTTP 200 and `status: false` (e.g.
 * `teacher/grade-v1` → "You are not authorized to access this teacher route"),
 * which axios reports as success. Without this check a refused request renders
 * as a legitimately empty widget — indistinguishable from "no data yet".
 * Throwing lets the section's own catch mark it failed instead.
 */
const body = (res: any): any => {
  const payload = res?.data;
  if (payload && payload.status === false) {
    throw new Error(payload.message || "Request was rejected");
  }
  return payload?.data ?? null;
};

/** the equal-length window immediately before the given one (for trends) */
const previousWindow = (win: { from: string; to: string }) => {
  const ISO = "YYYY-MM-DD";
  const days = moment(win.to, ISO).diff(moment(win.from, ISO), "days") + 1;
  return {
    from: moment(win.from, ISO).subtract(days, "days").format(ISO),
    to: moment(win.from, ISO).subtract(1, "days").format(ISO),
  };
};

export interface CampusData {
  kpis: KpiDatum[];
  readerClasses: CampusDatum[];
  readerMaxBooks: number;
  teachers: TeacherDatum[];
  students: StudentDatum[];
  adoption: AdoptionView | null;
  mixDimensions: MixDimension[];
  content: ContentView | null;
  assignment: AssignmentView | null;
  comparison: CampusDatum[];
  teacherAdoption: CampusDatum[];
  growthRange: RangeDatum | null;
  growthMetrics: GrowthMetric[];
}

const EMPTY: CampusData = {
  kpis: [],
  readerClasses: [],
  readerMaxBooks: 1,
  teachers: [],
  students: [],
  adoption: null,
  mixDimensions: [],
  content: null,
  assignment: null,
  comparison: [],
  teacherAdoption: [],
  growthRange: null,
  growthMetrics: [],
};

/** one loading flag per widget so each skeleton clears on its own API. */
export type CampusSection =
  | "kpi"
  | "growth"
  | "mix"
  | "campuses"
  | "adoption"
  | "teachers"
  | "students"
  | "content"
  | "teacherAdoption"
  | "comparison";

export const CAMPUS_SECTIONS: CampusSection[] = [
  "kpi",
  "growth",
  "mix",
  "campuses",
  "adoption",
  "teachers",
  "students",
  "content",
  "teacherAdoption",
  "comparison",
];

const allLoading = (): Record<CampusSection, boolean> =>
  CAMPUS_SECTIONS.reduce(
    (a, s) => ({ ...a, [s]: true }),
    {} as Record<CampusSection, boolean>,
  );

const MIX_TYPES = "genre,language,age_range,keyword";
const GROWTH_METRICS =
  "books_read,pages_read,cumulative_reading,learning_rate,book_completion_rate,avg_books_per_active_student,engagement";

/**
 * Fetches every campus `*-v1` endpoint for the selected date range. Each section
 * resolves independently and clears its own `loading` flag as soon as its API
 * responds, so the dashboard reveals widgets one-by-one behind their skeletons.
 * On a range change the last good data stays on screen and updates in place.
 *
 * `campusId` scopes every request to a single campus — used when a head office
 * opens one campus from /schools/campusDetail/:id. Without it the endpoints
 * resolve the scope from the logged-in account, as they do for a campus admin.
 */
export function useCampusData(
  range: { from: string; to: string },
  campusId?: number | string | null,
) {
  const [data, setData] = useState<CampusData>(EMPTY);
  const [loading, setLoading] =
    useState<Record<CampusSection, boolean>>(allLoading);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(() => {
    setError(null);
    setLoading(allLoading());
    const win: { from: string; to: string; campus_id?: number | string } = {
      from: range.from,
      to: range.to,
    };
    if (campusId) win.campus_id = campusId;

    const done = (sec: CampusSection) =>
      setLoading((p) => (p[sec] === false ? p : { ...p, [sec]: false }));

    let settled = 0;
    let failed = 0;
    const total = CAMPUS_SECTIONS.length;
    const settleTick = (ok: boolean) => {
      settled += 1;
      if (!ok) failed += 1;
      if (settled === total && failed === total) {
        setError("We couldn't load the campus analytics. Please retry.");
      }
    };

    // build the request lazily so a synchronous throw (e.g. misconfigured
    // endpoint) is caught here instead of crashing the whole dashboard.
    const run = (
      sec: CampusSection,
      request: () => Promise<any>,
      apply: (payload: any) => void,
    ) =>
      Promise.resolve()
        .then(request)
        .then((res) => {
          apply(body(res));
          done(sec);
          settleTick(true);
        })
        .catch(() => {
          done(sec);
          settleTick(false);
        });

    run("kpi", () => getDashboard(win), (d) =>
      setData((s) => ({ ...s, kpis: mapKpis(d) })),
    );
    // Reader classes + a real per-class Trend. `reader-class-v1` has no trend
    // field, so we fetch the immediately-preceding window of the same length
    // and compare books read per class.
    Promise.resolve()
      .then(async () => {
        const prev = previousWindow(win);
        const [curRes, prevRes] = await Promise.all([
          getReaderClass({ ...win, per_page: 8, page: 1 }),
          // spread `win` first so the campus scope survives the window swap
          getReaderClass({ ...win, ...prev, per_page: 50, page: 1 }).catch(
            () => null,
          ),
        ]);
        return { cur: body(curRes), prev: prevRes ? body(prevRes) : null };
      })
      .then(({ cur, prev }) => {
        const r = mapReaderClasses(cur);
        const before = new Map<number, number>();
        (prev?.classes ?? prev?.campuses ?? []).forEach((c: any) => {
          const id = c.class_id ?? c.campus_id;
          if (id != null) before.set(id, Number(c.total_books_read) || 0);
        });
        const classes = r.classes.map((c) => {
          const was = c.campus_id != null ? (before.get(c.campus_id) ?? 0) : 0;
          const now = c.books;
          const trend =
            was > 0 ? Math.round(((now - was) / was) * 100) : now > 0 ? 100 : 0;
          return { ...c, trend };
        });
        setData((s) => ({
          ...s,
          readerClasses: classes,
          readerMaxBooks: r.maxBooks,
        }));
        done("campuses");
        settleTick(true);
      })
      .catch(() => {
        done("campuses");
        settleTick(false);
      });
    run("teachers", () => getTopTeachers({ ...win, per_page: 4, page: 1 }), (d) =>
      setData((s) => ({ ...s, teachers: mapTopTeachers(d) })),
    );
    run("students", () => getTopStudents({ ...win, per_page: 4, page: 1 }), (d) =>
      setData((s) => ({ ...s, students: mapTopStudents(d) })),
    );
    run("adoption", () => getAdoptionRate(win), (d) =>
      setData((s) => ({ ...s, adoption: d ? mapAdoption(d) : null })),
    );
    run("mix", () => getReadingMix(MIX_TYPES, win), (d) =>
      setData((s) => ({ ...s, mixDimensions: mapReadingMix(d) })),
    );
    run(
      "content",
      () => getBookContentAnalysis({ ...win, per_page: 4, page: 1 }),
      (d) =>
        setData((s) => ({
          ...s,
          content: mapContent(d),
          assignment: mapAssignmentPipeline(d),
        })),
    );
    run(
      "comparison",
      () => getClassComparison({ ...win, per_page: 8, page: 1 }),
      (d) => setData((s) => ({ ...s, comparison: mapComparison(d) })),
    );
    run(
      "teacherAdoption",
      () => getTeacherAdoption({ ...win, per_page: 5, page: 1 }),
      (d) => setData((s) => ({ ...s, teacherAdoption: mapTeacherAdoption(d) })),
    );
    run("growth", () => getReadingGrowthTrend(GROWTH_METRICS, win), (d) => {
      const g = mapGrowth(d);
      setData((s) => ({ ...s, growthRange: g.range, growthMetrics: g.metrics }));
    });
  }, [range.from, range.to, campusId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, refetch: fetchAll };
}
